import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Bed, Bath, Square, ArrowRight, Building2, Home as HomeIcon, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { API, BACKEND_URL, formatPrice } from "@/lib/site";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";
const ANY = "__any__";

export default function Projek() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(ANY);
  const [ptype, setPtype] = useState(ANY);
  const [maxPrice, setMaxPrice] = useState("");

  useEffect(() => {
    document.title = "Projek Perumahan | dausREALTY";
    axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const locations = useMemo(() => Array.from(new Set(projects.map((p) => p.location).filter(Boolean))).sort(), [projects]);
  const types = useMemo(() => Array.from(new Set(projects.map((p) => p.property_type).filter(Boolean))).sort(), [projects]);

  const filtered = useMemo(() => {
    const maxNum = maxPrice ? Number(String(maxPrice).replace(/[^0-9]/g, "")) : null;
    return projects.filter((p) => {
      if (location !== ANY && p.location !== location) return false;
      if (ptype !== ANY && p.property_type !== ptype) return false;
      if (maxNum) {
        const pf = Number(String(p.price_from || "").replace(/[^0-9]/g, ""));
        if (!pf || pf > maxNum) return false;
      }
      return true;
    });
  }, [projects, location, ptype, maxPrice]);

  const clearFilters = () => { setLocation(ANY); setPtype(ANY); setMaxPrice(""); };
  const hasFilter = location !== ANY || ptype !== ANY || !!maxPrice;

  return (
    <div className="min-h-screen bg-white" data-testid="projek-page">
      <Navbar solid />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#1D4ED8]">
              <Building2 className="h-4 w-4" /> Projek Perumahan
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A2540]">
              Projek hartanah pilihan di Malaysia
            </h1>
            <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
              Terokai senarai projek perumahan terkini. Hubungi saya melalui WhatsApp untuk butiran lanjut &amp; tempahan.
            </p>
          </Reveal>

          {projects.length > 0 && (
            <Reveal delay={0.1}>
              <div className="mt-10 rounded-2xl border border-slate-200 bg-[#F8F9FA] p-5 md:p-6" data-testid="projek-filters">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="h-4 w-4 text-[#0A2540]" />
                  <span className="text-sm font-semibold text-[#0A2540]">Cari mengikut kehendak anda</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Lokasi</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="filter-location"><SelectValue placeholder="Semua Lokasi" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ANY}>Semua Lokasi</SelectItem>
                        {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Jenis Hartanah</Label>
                    <Select value={ptype} onValueChange={setPtype}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="filter-type"><SelectValue placeholder="Semua Jenis" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ANY}>Semua Jenis</SelectItem>
                        {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Harga Maksimum (RM)</Label>
                    <Input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="cth: 500,000" className="mt-1.5 bg-white" data-testid="filter-max-price" />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" onClick={clearFilters} disabled={!hasFilter}
                      data-testid="filter-clear-btn" className="w-full rounded-full border-slate-300 disabled:opacity-40">
                      <X className="mr-1 h-4 w-4" /> Set Semula
                    </Button>
                  </div>
                </div>
                {hasFilter && (
                  <p className="mt-4 text-sm text-slate-500" data-testid="filter-count">
                    Memaparkan <strong className="text-[#0A2540]">{filtered.length}</strong> daripada {projects.length} projek
                  </p>
                )}
              </div>
            </Reveal>
          )}

          {loading ? (
            <p className="mt-16 text-slate-500">Memuatkan projek...</p>
          ) : projects.length === 0 ? (
            <div className="mt-16 rounded-2xl border border-dashed border-slate-300 py-20 text-center" data-testid="projek-empty">
              <HomeIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-slate-500">Belum ada projek disenaraikan.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-dashed border-slate-300 py-16 text-center" data-testid="projek-filter-empty">
              <p className="text-slate-500">Tiada projek sepadan dengan penapis anda.</p>
              <Button onClick={clearFilters} variant="outline" className="mt-4 rounded-full">Set Semula Penapis</Button>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((p, i) => {
                const cover = p.images && p.images.length > 0 ? `${BACKEND_URL}/api/projects/${p.id}/images/0` : FALLBACK;
                const price = p.price_to ? `RM ${formatPrice(p.price_from)} - ${formatPrice(p.price_to)}` : p.price_from ? `Dari RM ${formatPrice(p.price_from)}` : "Harga atas permintaan";
                return (
                  <Reveal key={p.id} delay={i * 0.06}>
                    <Link to={`/projek/${p.slug}`} data-testid={`projek-card-${p.slug}`}
                      className="group flex flex-col h-full rounded-2xl border border-slate-200 overflow-hidden transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#1D4ED8]">
                      <div className="relative h-52 bg-slate-100 overflow-hidden">
                        <img src={cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        {p.status && <span className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold text-[#0A2540]">{p.status}</span>}
                      </div>
                      <div className="flex flex-col flex-1 p-6">
                        <h2 className="font-heading text-lg font-bold text-[#0A2540] leading-snug">{p.title}</h2>
                        {p.location && <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {p.location}</p>}
                        <p className="mt-3 font-heading text-lg font-bold text-[#1D4ED8]">{price}</p>
                        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                          {p.bedrooms && <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {p.bedrooms}</span>}
                          {p.bathrooms && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {p.bathrooms}</span>}
                          {p.built_up && <span className="flex items-center gap-1"><Square className="h-3.5 w-3.5" /> {p.built_up}</span>}
                        </div>
                        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#1D4ED8]">
                          Lihat Butiran <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
